import { useState, useEffect } from 'react'
import { contactGroupsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Modal, DataTable } from '../components/ui'
import { Formik, Form } from 'formik'
import { FormikInput, FormikTextarea } from '../components/ui'
import * as Yup from 'yup'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .max(191, 'Name must be at most 191 characters'),
  description: Yup.string().nullable(),
})

const ContactGroupsPage = () => {
  const [contactGroups, setContactGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')

  // Load contact groups on mount and when filters change
  useEffect(() => {
    loadContactGroups()
  }, [searchQuery, currentPage, perPage, sortBy, sortOrder])

  const loadContactGroups = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        per_page: perPage,
        page: currentPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      }
      if (searchQuery) {
        params.search = searchQuery
      }

      const response = await contactGroupsAPI.getAll(params)

      if (response.data.success) {
        const data = response.data.data
        setContactGroups(data.contact_groups || [])
        setPagination(data.pagination || pagination)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contact groups')
      setContactGroups([])
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (column, order) => {
    setSortBy(column)
    setSortOrder(order)
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  const handlePageSizeChange = (newPerPage) => {
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page when page size changes
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleCreate = () => {
    setEditingGroup(null)
    setShowModal(true)
  }

  const handleEdit = (group) => {
    setEditingGroup(group)
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact group?')) {
      return
    }

    try {
      setDeletingId(id)
      setError(null)
      const response = await contactGroupsAPI.delete(id)

      if (response.data.success) {
        setSuccess('Contact group deleted successfully')
        await loadContactGroups()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete contact group')
      setTimeout(() => setError(null), 5000)
    } finally {
      setDeletingId(null)
    }
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null)
      setSuccess(null)

      if (editingGroup) {
        // Update
        const response = await contactGroupsAPI.update(editingGroup.id, values)
        if (response.data.success) {
          setSuccess('Contact group updated successfully')
          setShowModal(false)
          resetForm()
          await loadContactGroups()
          setTimeout(() => setSuccess(null), 3000)
        }
      } else {
        // Create
        const response = await contactGroupsAPI.create(values)
        if (response.data.success) {
          setSuccess('Contact group created successfully')
          setShowModal(false)
          resetForm()
          await loadContactGroups()
          setTimeout(() => setSuccess(null), 3000)
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingGroup ? 'update' : 'create'} contact group`)
      setTimeout(() => setError(null), 5000)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Contact Groups</h1>
        <p className="text-gray-600 mt-1">
          Manage your contact groups.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="md:w-1/3">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                id="search"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Search by name or description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Button variant="primary" onClick={handleCreate}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Contact Group
            </Button>
          </div>

          {contactGroups.length === 0 && !loading ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">No contact groups found</p>
              <p className="text-sm mb-4">Get started by adding your first contact group</p>
              <Button variant="primary" onClick={handleCreate}>
                Add Contact Group
              </Button>
            </div>
          ) : (
            <DataTable
              columns={[
                {
                  key: 'name',
                  label: 'Name',
                  sortable: true,
                },
                {
                  key: 'description',
                  label: 'Description',
                  sortable: true,
                  nowrap: false,
                },
                {
                  key: 'contacts_count',
                  label: 'Contacts Count',
                  sortable: true,
                  render: (group) => group.contacts_count || 0,
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  render: (group) => (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(group)}
                        className="text-blue-600 hover:text-blue-900 font-medium transition-colors"
                      >
                        Edit
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => handleDelete(group.id)}
                        disabled={deletingId === group.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 font-medium transition-colors"
                      >
                        {deletingId === group.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  ),
                },
              ]}
              data={contactGroups}
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
              loading={loading}
              perPage={perPage}
            />
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingGroup(null)
        }}
        title={editingGroup ? 'Edit Contact Group' : 'Add Contact Group'}
        size="lg"
      >
        <Formik
          initialValues={{
            name: editingGroup?.name || '',
            description: editingGroup?.description || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <FormikInput
                id="name"
                name="name"
                label="Name"
                placeholder="Enter contact group name"
                required
              />

              <FormikTextarea
                id="description"
                name="description"
                label="Description"
                placeholder="Enter description (optional)"
                rows={4}
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false)
                    setEditingGroup(null)
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {editingGroup ? 'Update' : 'Create'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default ContactGroupsPage

